import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe, Eye, Type, Contrast, Underline, MoveVertical, Pause, Focus, RotateCcw } from "lucide-react";
import { useSite } from "../../lib/site-context";
import { useA11y } from "../../lib/a11y-context";
import { ASSET_URL } from "../../lib/api";

const NAV_ITEMS = [
  { slug: "home", path: "/" },
  { slug: "workflow", path: "/workflow" },
  { slug: "leistungen", path: "/leistungen" },
  { slug: "veranstaltungen", path: "/ueber-uns/veranstaltungen" },
  { slug: "ueber-uns", path: "/ueber-uns" },
  { slug: "presse", path: "/presse" },
  { slug: "wissen", path: "/wissen" },
  { slug: "kontakt", path: "/kontakt" },
];

export default function Header() {
  const { settings, pages, lang, t } = useSite();
  const { prefs, toggle, reset } = useA11y();
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();

  React.useEffect(() => { setOpen(false); }, [loc.pathname]);
  const languageHref = React.useMemo(() => {
    const params = new URLSearchParams(loc.search);
    if (params.get("lang") === "en") params.delete("lang");
    else params.set("lang", "en");
    const search = params.toString();
    return `${loc.pathname}${search ? `?${search}` : ""}${loc.hash || ""}`;
  }, [loc.pathname, loc.search, loc.hash]);

  const pageBySlug = React.useMemo(() => Object.fromEntries((pages || []).map(p => [p.slug, p])), [pages]);
  const labelFor = (item) => {
      if (item.slug === "presse") return lang === "en" ? "Press" : "Presse";
    const p = pageBySlug[item.slug];
    if (p) return lang === "en" ? p.nav_label_en : p.nav_label_de;
    if (item.slug === "preisliste") return lang === "en" ? "Pricing" : "Preise";
    if (item.slug === "veranstaltungen") return lang === "en" ? "Events" : "Veranstaltungen";
    if (item.slug === "kontakt") return lang === "en" ? "Contact" : "Kontakt";
    return item.slug;
  };

  return (
    <>
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50"
      style={{
        background: "rgba(236,235,229,0.78)",
        WebkitBackdropFilter: "blur(20px) saturate(160%)",
        backdropFilter: "blur(20px) saturate(160%)",
        borderBottom: "1px solid var(--line-soft)",
      }}
    >
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-[11px] md:py-[15px] flex items-center gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
          <img
            src={settings?.logo_media_id ? ASSET_URL(`/api/public/media/file/originals/${settings?.logo_filename || ""}`) : "/brand/logo.svg"}
            alt="Dr. B.-Digitec"
            className="h-14 md:h-[72px] w-auto transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </Link>

        <nav className="hidden lg:flex items-center justify-center gap-0 flex-1 min-w-0" aria-label="Hauptnavigation">
          {NAV_ITEMS.map((item) => (
            item.slug === "leistungen" ? (
              <div key={item.slug} className="relative group">
                <NavLink
                  to={item.path}
                  end
                  data-testid="nav-leistungen"
                  className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                >
                  {labelFor(item)}
                </NavLink>
                <div className="absolute left-0 top-full pt-3 hidden group-hover:block group-focus-within:block min-w-[210px] z-50">
                  <div className="border-2 border-[var(--ink)] bg-[var(--bg)] py-2" style={{ boxShadow: "var(--neu-raised-sm)" }}>
                    <NavLink to="/leistungen" className="block px-4 py-2.5 font-mono text-[12px] tracking-[0.14em] uppercase hover:bg-black/5">{t("Leistungen", "Services")}</NavLink>
                    <NavLink to="/preisliste" data-testid="nav-preisliste" className="block px-4 py-2.5 font-mono text-[12px] tracking-[0.14em] uppercase hover:bg-black/5">{t("Preise", "Pricing")}</NavLink>
                  </div>
                </div>
              </div>
            ) : item.slug === "ueber-uns" ? (
              <div key={item.slug} className="relative group">
                <NavLink
                  to={item.path}
                  end
                  data-testid={`nav-${item.slug}`}
                  className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                >
                  {labelFor(item)}
                </NavLink>
                <div className="absolute left-0 top-full pt-3 hidden group-hover:block group-focus-within:block min-w-[210px] z-50">
                  <div className="border-2 border-[var(--ink)] bg-[var(--bg)] py-2" style={{ boxShadow: "var(--neu-raised-sm)" }}>
                    <NavLink to="/ueber-uns" className="block px-4 py-2.5 font-mono text-[12px] tracking-[0.14em] uppercase hover:bg-black/5">{t("Über uns", "About")}</NavLink>
                    <NavLink to="/wissen/news" data-testid="nav-news" className="block px-4 py-2.5 font-mono text-[12px] tracking-[0.14em] uppercase hover:bg-black/5">{t("News", "News")}</NavLink>
                    <NavLink to="/partner" data-testid="nav-partner" className="block px-4 py-2.5 font-mono text-[12px] tracking-[0.14em] uppercase hover:bg-black/5">{t("Partner", "Partners")}</NavLink>
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.slug}
                to={item.path}
                data-testid={item.slug === "veranstaltungen" ? "nav-events" : `nav-${item.slug}`}
                end={item.path === "/"}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              >
                {labelFor(item)}
              </NavLink>
            )
          ))}
        </nav>

        <div className="ml-auto lg:ml-0 flex items-center gap-2 shrink-0">
          <div className="hidden lg:flex items-center">
            <Link to="/praxis-check" data-testid="nav-praxis-check-btn" className="inline-flex brut-btn !py-3 !px-5 text-[13px]">
              {t("Praxis-Check", "Practice-Check")}
            </Link>
          </div>
          <a
            href={languageHref}
            data-testid="lang-toggle"
            className="hidden md:inline-flex items-center gap-1.5 font-bold text-[13px] px-3 py-2.5 rounded-xl hover:bg-black/5 transition-colors"
            aria-label="Sprache wechseln"
          >
            <Globe size={14} /> {lang === "de" ? "EN" : "DE"}
          </a>
          <button
            data-testid="mobile-menu-burger"
            onClick={() => setOpen(true)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-2xl"
            style={{ boxShadow: "var(--neu-raised-sm)" }}
            aria-label="Menü öffnen"
          >
            <Menu size={22} />
          </button>
        </div>
      </div>
    </header>

      {open && (
        <div data-testid="mobile-menu-overlay" className="fixed inset-0 z-[60] bg-[var(--bg)] overflow-y-auto">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between border-b border-[var(--line-soft)]">
            <img src="/brand/logo.svg" alt="Dr. B.-Digitec" className="h-12 w-auto" />
            <button
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center w-11 h-11 rounded-2xl"
              style={{ boxShadow: "var(--neu-raised-sm)" }}
              data-testid="mobile-menu-close"
              aria-label="Menü schließen"
            >
              <X size={22} />
            </button>
          </div>

            <nav className="drb-mobile-menu" aria-label="Mobile Navigation">
              {NAV_ITEMS.map((item) => (
                <React.Fragment key={item.slug}>
                  <Link
                    to={item.path}
                    onClick={() => setOpen(false)}
                    className="drb-mobile-link"
                    data-testid={item.slug === "veranstaltungen" ? "mnav-events" : `mnav-${item.slug}`}
                  >
                    <span>{labelFor(item)}</span>
                    <span aria-hidden="true">→</span>
                  </Link>

                  {item.slug === "leistungen" && (
                    <Link
                      to="/preisliste"
                      onClick={() => setOpen(false)}
                      className="drb-mobile-link drb-mobile-sub"
                      data-testid="mnav-preisliste"
                    >
                      <span>{t("Preise", "Pricing")}</span>
                      <span aria-hidden="true">→</span>
                    </Link>
                  )}

                  {item.slug === "ueber-uns" && (
                    <>
                      <Link
                        to="/wissen/news"
                        onClick={() => setOpen(false)}
                        className="drb-mobile-link drb-mobile-sub"
                        data-testid="mnav-news"
                      >
                        <span>{t("News", "News")}</span>
                        <span aria-hidden="true">→</span>
                      </Link>
                      <Link
                        to="/partner"
                        onClick={() => setOpen(false)}
                        className="drb-mobile-link drb-mobile-sub"
                        data-testid="mnav-partner"
                      >
                        <span>{t("Partner", "Partners")}</span>
                        <span aria-hidden="true">→</span>
                      </Link>
                    </>
                  )}
                </React.Fragment>
              ))}

              <Link
                to="/praxis-check"
                onClick={() => setOpen(false)}
                className="drb-mobile-cta"
                data-testid="mnav-praxis-check"
              >
                {t("Praxis-Check starten", "Start Practice-Check")}
              </Link>

              <a
                href={languageHref}
                className="drb-mobile-lang"
                style={{ boxShadow: "var(--neu-raised-sm)" }}
                data-testid="mnav-lang-toggle"
              >
                <Globe size={14} /> {lang === "de" ? "Switch to English" : "Auf Deutsch wechseln"}
              </a>
            </nav>

          <section className="px-5 md:px-10 py-10 border-t border-[var(--line-soft)]">
            <div className="brut-tag mb-5"><Eye size={12} /> {t("Ansicht anpassen", "Adjust view")}</div>
            <h3 className="brut-h3 mb-6">{t("Barrierefreiheit", "Accessibility")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["large", t("Schrift größer", "Larger text"), Type],
                ["xlarge", t("Schrift extra groß", "Extra large text"), Type],
                ["highContrast", t("Hoher Kontrast", "High contrast"), Contrast],
                ["underlineLinks", t("Links unterstreichen", "Underline links"), Underline],
                ["lineHeight", t("Zeilenhöhe erhöhen", "Increase line-height"), MoveVertical],
                ["noMotion", t("Animationen reduzieren", "Reduce motion"), Pause],
                ["noBgVideo", t("Hintergrundvideos stoppen", "Stop background videos"), Pause],
                ["strongFocus", t("Fokusrahmen verstärken", "Stronger focus ring"), Focus],
              ].map(([key, label, Icon]) => (
                <button
                  key={key}
                  onClick={() => toggle(key)}
                  data-testid={`a11y-${key}`}
                  className={`flex items-center justify-between px-4 py-3 text-[13px] font-bold rounded-2xl ${prefs[key] ? "bg-[var(--ink)] text-[var(--bg)]" : ""}`}
                  style={prefs[key] ? { boxShadow: "var(--neu-inset-sm)" } : { boxShadow: "var(--neu-raised-sm)" }}
                  aria-pressed={prefs[key]}
                >
                  <span className="flex items-center gap-3"><Icon size={14} /> {label}</span>
                  <span>{prefs[key] ? "ON" : "OFF"}</span>
                </button>
              ))}
            </div>
            <button onClick={reset} className="mt-4 inline-flex items-center gap-2 font-bold text-sm underline">
              <RotateCcw size={12} /> {t("Zurücksetzen", "Reset")}
            </button>
          </section>
        </div>
      )}
    </>
  );
}
